/** Weekday labels for Strapi price `days_of_week` (comma-separated MON,TUE,…). */
const WEEKDAY_LABELS = {
    MON: "Mon",
    TUE: "Tue",
    WED: "Wed",
    THU: "Thu",
    FRI: "Fri",
    SAT: "Sat",
    SUN: "Sun",
};

const STANDARD_DURATIONS = [15, 30, 60];

function resolveRelationId(value) {
    if (value == null) {
        return null;
    }
    if (typeof value === "number" || typeof value === "string") {
        return Number(value);
    }
    if (typeof value === "object" && value.id != null) {
        return Number(value.id);
    }
    return null;
}

function isAllDays(daysOfWeek) {
    return !daysOfWeek || `${daysOfWeek}`.trim() === "";
}

function formatDaysOfWeekLabel(daysOfWeek) {
    if (isAllDays(daysOfWeek)) {
        return "All days";
    }
    const codes = `${daysOfWeek}`
        .split(",")
        .map((part) => part.trim().toUpperCase())
        .filter((code) => WEEKDAY_LABELS[code]);
    if (!codes.length) {
        return "All days";
    }
    return codes.map((code) => WEEKDAY_LABELS[code]).join(", ");
}

function formatPlayerBandLabel(minPlayers, maxPlayers) {
    if (minPlayers != null && maxPlayers != null) {
        return `${minPlayers}–${maxPlayers} players`;
    }
    if (minPlayers != null) {
        return `${minPlayers}+ players`;
    }
    if (maxPlayers != null) {
        return `up to ${maxPlayers} players`;
    }
    return "";
}

function currencySymbol(code) {
    if (code === "USD") {
        return "$";
    }
    if (code === "GBP") {
        return "£";
    }
    return "€";
}

function formatAmount(amountInCents, currency) {
    const amount = (Number(amountInCents) || 0) / 100;
    return `${currencySymbol(currency)}${amount.toFixed(2)}`;
}

function formatEuroCents(amountInCents) {
    return `€${((Number(amountInCents) || 0) / 100).toFixed(2)}`;
}

function hasPlayerBand(minPlayers, maxPlayers) {
    return minPlayers != null || maxPlayers != null;
}

function selectPricesForLocation(allPrices, locationId) {
    const locationPrices = allPrices.filter(
        (price) => resolveRelationId(price.location) === Number(locationId),
    );
    if (locationPrices.length > 0) {
        return { prices: locationPrices, source: "location" };
    }
    const globalPrices = allPrices.filter((price) => resolveRelationId(price.location) == null);
    return { prices: globalPrices, source: "default" };
}

function buildDefaultBaseByDuration(allPrices) {
    const globalPrices = allPrices.filter((price) => resolveRelationId(price.location) == null);
    const map = new Map();
    for (const price of globalPrices) {
        if (!isBaseTier(price) || !price.duration) {
            continue;
        }
        map.set(Number(price.duration), price);
    }
    return map;
}

function advisoryLabelIfDifferent(price, defaultBaseByDuration) {
    if (!price?.duration) {
        return null;
    }
    const locationCurrency = (price.currency || "EUR").toUpperCase();
    if (locationCurrency !== "EUR") {
        return null;
    }
    const advice = defaultBaseByDuration.get(Number(price.duration));
    if (!advice) {
        return null;
    }
    const adviceCurrency = (advice.currency || "EUR").toUpperCase();
    if (adviceCurrency !== "EUR") {
        return null;
    }
    const sameAmount = Number(price.amount_in_cents) === Number(advice.amount_in_cents);
    const sameUnit = Boolean(price.price_per_player) === Boolean(advice.price_per_player);
    if (sameAmount && sameUnit) {
        return null;
    }
    const unit = advice.price_per_player ? "pp" : "groep";
    return {
        amountLabel: formatEuroCents(advice.amount_in_cents),
        unit,
        text: `advice ${formatEuroCents(advice.amount_in_cents)} ${unit}`,
    };
}

function isDurationTier(price) {
    return resolveRelationId(price.experience) == null;
}

function isBaseTier(price) {
    return (
        isDurationTier(price) &&
        isAllDays(price.days_of_week) &&
        !hasPlayerBand(price.min_number_of_players, price.max_number_of_players)
    );
}

function isDayRule(price) {
    return isDurationTier(price) && !isAllDays(price.days_of_week);
}

function isPlayerBandRule(price) {
    return (
        isDurationTier(price) &&
        isAllDays(price.days_of_week) &&
        hasPlayerBand(price.min_number_of_players, price.max_number_of_players)
    );
}

function toDisplayRow(price, experienceNameById, defaultBaseByDuration = null, compareToAdvice = false) {
    const currency = (price.currency || "EUR").toUpperCase();
    const unit = price.price_per_player ? "per player" : "per group";
    const experienceId = resolveRelationId(price.experience);
    const experienceName = experienceId != null
        ? experienceNameById.get(String(experienceId)) || `Experience #${experienceId}`
        : null;
    const advisory = compareToAdvice
        ? advisoryLabelIfDifferent(price, defaultBaseByDuration || new Map())
        : null;

    return {
        id: price.id,
        durationMinutes: price.duration,
        durationLabel: `${price.duration} min`,
        amountLabel: formatAmount(price.amount_in_cents, currency),
        unit,
        currency,
        daysLabel: formatDaysOfWeekLabel(price.days_of_week),
        playersLabel: formatPlayerBandLabel(
            price.min_number_of_players,
            price.max_number_of_players,
        ),
        experienceName,
        pricePerPlayer: Boolean(price.price_per_player),
        advisoryLabel: advisory?.text || null,
        advisoryAmountLabel: advisory?.amountLabel || null,
    };
}

/**
 * Build a clear location pricing view model (same shape as location-reports tiers).
 */
export function buildLocationPricingViewModel({
    allPrices,
    locationId,
    locationName,
    experienceNameById,
}) {
    const { prices, source } = selectPricesForLocation(allPrices, locationId);
    if (!prices.length) {
        return {
            locationName,
            source,
            currency: "EUR",
            pricingModel: null,
            baseTiers: [],
            dayRules: [],
            playerBandRules: [],
            experienceOverrides: [],
            empty: true,
        };
    }

    const defaultBaseByDuration = buildDefaultBaseByDuration(allPrices);
    const compareToAdvice = source === "location";

    const baseByDuration = new Map();
    for (const price of prices) {
        if (!isBaseTier(price) || !price.duration) {
            continue;
        }
        baseByDuration.set(price.duration, price);
    }

    const baseTiers = STANDARD_DURATIONS.map((duration) => {
        const price = baseByDuration.get(duration);
        if (!price) {
            return null;
        }
        return toDisplayRow(price, experienceNameById, defaultBaseByDuration, compareToAdvice);
    }).filter(Boolean);

    // Include non-standard duration base tiers (e.g. 45 min) after standards.
    for (const price of prices) {
        if (!isBaseTier(price) || !price.duration) {
            continue;
        }
        if (STANDARD_DURATIONS.includes(price.duration)) {
            continue;
        }
        baseTiers.push(toDisplayRow(price, experienceNameById, defaultBaseByDuration, compareToAdvice));
    }
    baseTiers.sort((a, b) => a.durationMinutes - b.durationMinutes);

    const dayRules = prices
        .filter(isDayRule)
        .map((price) => toDisplayRow(price, experienceNameById, defaultBaseByDuration, compareToAdvice))
        .sort((a, b) => a.durationMinutes - b.durationMinutes);

    const playerBandRules = prices
        .filter(isPlayerBandRule)
        .map((price) => toDisplayRow(price, experienceNameById, defaultBaseByDuration, compareToAdvice))
        .sort((a, b) => a.durationMinutes - b.durationMinutes);

    const experienceOverrides = prices
        .filter((price) => resolveRelationId(price.experience) != null)
        .map((price) => toDisplayRow(price, experienceNameById, defaultBaseByDuration, compareToAdvice))
        .sort((a, b) => (a.experienceName || "").localeCompare(b.experienceName || ""));

    const sample = prices.find((price) => price.currency) || prices[0];
    const pricingModel = sample?.price_per_player ? "Per player" : "Per group";

    return {
        locationName,
        source,
        currency: (sample?.currency || "EUR").toUpperCase(),
        pricingModel,
        baseTiers,
        dayRules,
        playerBandRules,
        experienceOverrides,
        empty: baseTiers.length === 0
            && dayRules.length === 0
            && playerBandRules.length === 0
            && experienceOverrides.length === 0,
    };
}
